<script setup lang="ts">
/**
 * User-configurable bottom navigation bar for mobile/tablet (< lg).
 *
 * Renders the dashboard plus the user's pinned items (useMobileNavbar). When
 * pinned items exceed maxButtons, the last slot becomes a "More" button that
 * opens a bottom sheet with the remainder. Honors iOS/Android safe-area
 * insets via env(safe-area-inset-bottom) — requires viewport-fit=cover.
 */
import { Link, usePage } from "@inertiajs/vue3";
import { MoreHorizontal } from "@lucide/vue";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerTitle,
} from "@/components/ui/drawer";

import { useMobileNavbar, type NavbarCandidate } from "../composables/useMobileNavbar";
import { resolveLucideIcon } from "../utils/lucideIcon";
import { isInternalPortalRoute, isNavRouteActive, translateNavLabel } from "../utils/navigation";

const props = defineProps<{
    /** Current portal route prefix (e.g. '/crud', '' for root) — used by isInternalRoute. */
    routePrefix: string;
}>();

const page = usePage();
const { t: translate, te: translationExists } = useI18n();

const { settings, dashboard, displayedItems } = useMobileNavbar();

const currentUrl = computed(() => page.url);

function isRouteActive(route: string): boolean {
    return isNavRouteActive(currentUrl.value, route);
}

const isOnDashboard = computed(() => {
    const route = dashboard.value.route;
    return currentUrl.value === route || currentUrl.value === route + "/";
});

function isInternalRoute(route: string, item?: NavbarCandidate): boolean {
    return isInternalPortalRoute(props.routePrefix, route, item);
}

function translateLabel(item: NavbarCandidate): string {
    return translateNavLabel(translate, translationExists, item);
}

const dashboardLabel = computed(() => {
    const config = dashboard.value;
    if (config.labelKey && translationExists(config.labelKey)) return translate(config.labelKey);
    return config.label ?? translate("portal.dashboard");
});

/**
 * Slot math: dashboard always occupies slot 1. When the remaining pinned
 * items don't fit, the last slot is reserved for "More".
 */
const inlineItems = computed((): NavbarCandidate[] => {
    const slotsForItems = settings.value.maxButtons - 1;
    if (displayedItems.value.length <= slotsForItems) return displayedItems.value;
    return displayedItems.value.slice(0, slotsForItems - 1);
});

const overflowItems = computed((): NavbarCandidate[] => {
    const slotsForItems = settings.value.maxButtons - 1;
    if (displayedItems.value.length <= slotsForItems) return [];
    return displayedItems.value.slice(slotsForItems - 1);
});

const moreOpen = ref(false);

// Close the sheet after navigating
watch(currentUrl, () => {
    moreOpen.value = false;
});

const itemClass = (active: boolean): string =>
    [
        "flex min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-lg px-1 py-1.5 text-[10px] font-medium transition-colors",
        active ? "text-primary" : "text-muted-foreground hover:text-foreground",
    ].join(" ");
</script>

<template>
    <nav
        v-if="settings.enabled"
        :aria-label="translate('portal.mobile_navbar.title')"
        class="mobile-footer-navbar bg-background/95 supports-[backdrop-filter]:bg-background/80 fixed inset-x-0 bottom-0 z-40 border-t backdrop-blur lg:hidden"
    >
        <div class="flex items-stretch px-1 py-1">
            <!-- Dashboard: always first -->
            <a
                v-if="!isInternalRoute(dashboard.route)"
                :href="dashboard.route"
                :class="itemClass(isOnDashboard)"
            >
                <component :is="resolveLucideIcon(dashboard.icon)" class="size-5 shrink-0" />
                <span class="w-full truncate text-center">{{ dashboardLabel }}</span>
            </a>
            <Link v-else :href="dashboard.route" :class="itemClass(isOnDashboard)">
                <component :is="resolveLucideIcon(dashboard.icon)" class="size-5 shrink-0" />
                <span class="w-full truncate text-center">{{ dashboardLabel }}</span>
            </Link>

            <!-- Pinned items -->
            <template v-for="item in inlineItems" :key="item.key">
                <Link
                    v-if="isInternalRoute(item.route, item)"
                    :href="item.route"
                    :class="itemClass(isRouteActive(item.route))"
                >
                    <component :is="resolveLucideIcon(item.icon)" class="size-5 shrink-0" />
                    <span class="w-full truncate text-center">{{ translateLabel(item) }}</span>
                </Link>
                <a v-else :href="item.route" :class="itemClass(isRouteActive(item.route))">
                    <component :is="resolveLucideIcon(item.icon)" class="size-5 shrink-0" />
                    <span class="w-full truncate text-center">{{ translateLabel(item) }}</span>
                </a>
            </template>

            <!-- More: overflow sheet trigger -->
            <button
                v-if="overflowItems.length > 0"
                type="button"
                :class="itemClass(moreOpen)"
                :aria-expanded="moreOpen"
                @click="moreOpen = true"
            >
                <MoreHorizontal class="size-5 shrink-0" />
                <span class="w-full truncate text-center">{{ translate('portal.mobile_navbar.more') }}</span>
            </button>
        </div>
    </nav>

    <Drawer v-model:open="moreOpen">
        <DrawerContent>
            <DrawerTitle class="px-4 pt-2 text-sm font-semibold">
                {{ translate('portal.mobile_navbar.more') }}
            </DrawerTitle>
            <DrawerDescription class="sr-only">
                {{ translate('portal.mobile_navbar.title') }}
            </DrawerDescription>
            <div class="grid grid-cols-4 gap-2 p-4 pb-[calc(1rem_+_env(safe-area-inset-bottom))]">
                <template v-for="item in overflowItems" :key="item.key">
                    <Link
                        v-if="isInternalRoute(item.route, item)"
                        :href="item.route"
                        :class="[
                            'flex flex-col items-center gap-2 rounded-lg p-3 text-xs transition-colors',
                            isRouteActive(item.route)
                                ? 'bg-primary/10 text-primary'
                                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                        ]"
                    >
                        <component :is="resolveLucideIcon(item.icon)" class="size-5 shrink-0" />
                        <span class="w-full truncate text-center">{{ translateLabel(item) }}</span>
                    </Link>
                    <a
                        v-else
                        :href="item.route"
                        class="text-muted-foreground hover:bg-muted hover:text-foreground flex flex-col items-center gap-2 rounded-lg p-3 text-xs transition-colors"
                    >
                        <component :is="resolveLucideIcon(item.icon)" class="size-5 shrink-0" />
                        <span class="w-full truncate text-center">{{ translateLabel(item) }}</span>
                    </a>
                </template>
            </div>
        </DrawerContent>
    </Drawer>
</template>

<style scoped>
/* Keep the bar clear of the iOS home indicator / Android gesture area */
.mobile-footer-navbar {
    padding-bottom: env(safe-area-inset-bottom, 0px);
}
</style>
